import React from 'react';
import { useTranslation } from 'react-i18next';
import { SheetSection } from '../settings/sheet';
import type { HomeData, PeriodKey } from './homeApi';
import { fmtEur, fmtMoney, fmtNum, fmtPct } from './format';
import { concentration, sum, unitsGap } from './insights';
import { HBarList } from './charts/HBarList';
import { StackedBar } from './charts/StackedBar';
import { KpiStrip, KpiItem } from './KpiStrip';
import { ProductsTable, deltaHeader } from './HomeSections';
import { Fig, Legend, Note, rich } from './ui';

/**
 * Αρχική → «Προϊόντα» (δείγμα a_products): τεμάχια, μέση τιμή, κορυφαία κατηγορία/προϊόν, κατηγορίες από
 * ΟΛΑ τα κανάλια (μαζί τα τραπέζια) και τα 10 πρώτα με «Δ» σε τεμάχια — από τα top_products/categories
 * του GET …/home. Τα «Χωρίς πωλήσεις» του δείγματος θέλουν δικό τους endpoint (δεν υπάρχει ακόμη).
 */
export const ProductsReport: React.FC<{ data: HomeData; periodKey: PeriodKey }> = ({ data, periodKey }) => {
  const { t } = useTranslation(['home']);
  const cats = [...data.categories].filter(c => c.quantity > 0 || c.revenue !== 0).sort((a, b) => b.revenue - a.revenue);
  const top = [...data.top_products].sort((a, b) => b.revenue - a.revenue).slice(0, 10);
  const units = cats.length > 0 ? sum(cats.map(c => c.quantity)) : sum(top.map(p => p.quantity));
  const revenue = cats.length > 0 ? sum(cats.map(c => c.revenue)) : sum(top.map(p => p.revenue));
  const c0 = cats[0];
  const p0 = top[0];
  const shareOf = (v: number) => (revenue > 0 ? (v / revenue) * 100 : 0);
  const gap = unitsGap(cats);
  const conc = concentration(top, revenue);

  const kpis: KpiItem[] = [
    { id: 'units', label: t('home:productsPage.kpiUnits'), value: fmtNum(units), hero: true },
    { id: 'avgPrice', label: t('home:productsPage.kpiAvgPrice'), value: units > 0 ? fmtMoney(revenue / units) : '—', unit: units > 0 ? '€' : undefined, rows: [t('home:productsPage.kpiAvgPriceSub')] },
    {
      id: 'topCategory', label: t('home:productsPage.kpiTopCategory'), value: c0 ? c0.name : '—', word: true,
      rows: c0 ? [t('home:productsPage.kpiTopCategorySub', { pct: fmtPct(shareOf(c0.revenue)) })] : [],
    },
    {
      id: 'topProduct', label: t('home:productsPage.kpiTopProduct'), value: p0 ? p0.name : '—', word: true,
      rows: p0 ? [t('home:productsPage.kpiTopProductSub', { units: fmtNum(p0.quantity), amount: fmtEur(p0.revenue) })] : [],
    },
  ];

  return (
    <>
      <KpiStrip items={kpis} ariaLabel={t('home:productsPage.title')} />
      <SheetSection
        id="products-categories"
        title={t('home:productsPage.catsTitle')}
        indexLabel={t('home:productsPage.catsIndex')}
        hint={t('home:productsPage.catsHint')}
        testId="section-categories"
        aside={c0 ? (
          <>
            <Fig value={fmtPct(shareOf(c0.revenue))} sub={t('home:productsPage.catsFigSub', { name: c0.name })} testId="categories-fig" />
            {gap && (
              <Note testId="categories-note">
                {rich(t('home:productsPage.catsGap', { name: gap.cat.name, units: fmtNum(gap.cat.quantity), pct: fmtPct(gap.revenueShare) }))}
              </Note>
            )}
          </>
        ) : undefined}
      >
        {cats.length > 0 ? (
          <HBarList
            ariaLabel={t('home:productsPage.catsTitle')}
            shareLabel={fmtPct}
            rows={cats.map(c => ({
              key: String(c.category_id ?? c.name),
              name: c.name,
              value: Math.max(0, c.revenue),
              share: shareOf(c.revenue),
              valueLabel: fmtEur(c.revenue),
              extraLabel: t('home:productsPage.unitsShort', { n: fmtNum(c.quantity) }),
              tip: `${c.name} · ${fmtEur(c.revenue)} · ${t('home:productsPage.unitsLong', { n: fmtNum(c.quantity) })} · ${fmtPct(shareOf(c.revenue))}`,
            }))}
          />
        ) : (
          <p className="xh-empty">{t('home:products.empty')}</p>
        )}
      </SheetSection>
      <SheetSection
        id="products-top"
        title={t('home:productsPage.topTitle')}
        indexLabel={t('home:productsPage.topIndex')}
        hint={t('home:productsPage.topHint')}
        testId="section-top-products"
        aside={conc ? (
          <>
            <Fig value={`${fmtNum(conc.top10Share)}%`} sub={t('home:productsPage.topFigSub')} testId="top-fig" />
            <div className="xh-ctl">
              <StackedBar
                thin
                parts={[
                  { key: 'top3', label: t('home:productsPage.conc3'), value: conc.top3, tone: 1 },
                  { key: 'next7', label: t('home:productsPage.conc10'), value: conc.next7, tone: 2 },
                  { key: 'rest', label: t('home:productsPage.concRest'), value: conc.rest, tone: 3 },
                ]}
              />
              <Legend
                items={[
                  { key: 'top3', label: t('home:productsPage.conc3'), swatch: 'bg1' },
                  { key: 'next7', label: t('home:productsPage.conc10'), swatch: 'bg2' },
                  { key: 'rest', label: t('home:productsPage.concRest'), swatch: 'bg3' },
                ]}
              />
            </div>
          </>
        ) : undefined}
      >
        {top.length > 0
          ? <ProductsTable rows={top} withDelta deltaTitle={deltaHeader(t, periodKey)} testId="top-products-table" />
          : <p className="xh-empty">{t('home:products.empty')}</p>}
      </SheetSection>
    </>
  );
};

export default ProductsReport;
